import React, { Fragment, Component } from 'react'
import Mesh from 'aria-viewer/players/three/Mesh'
import * as THREE from 'three'
import { THREEx } from 'aria-viewer/legacy/video/videoTexture'
import DomEvents from '../../../utils/domevents'
import CHECK_CIRCLE from 'public/assets/svg/check_circle.svg'
import { Sprite3D } from '../sprite3D'
import TriggerText3D from './TriggerText3D'
import { dispose3DObject } from '../../../utils'
import { HitSphere3D } from '../hitMesh3D'

const TRIGGER_RADIUS = 0.3

const geometry = new THREE.SphereGeometry(0, 32, 16)

const material = texture => {
    return new THREE.MeshBasicMaterial({
        map: texture,
    })
}

class Trigger3DBaseSphere extends Component {
    constructor(props) {
        super(props)
        // create a ref to store the textInput DOM element
        this.video
        this.sceneObj
        this.rootObj
        this.mixer = {
            mesh: null,
        }
        this.actions = {
            mesh: {},
        }
        this.clock = new THREE.Clock()
        if (props.video) {
            this.thumbnailMaterial
            this.videoMaterial
        }
    }

    state = {
        isHovering: false,
        texture: null,
        material: null,
        geometry,
        scale: {
            x: 1,
            y: 1,
            z: 1,
        },
    }

    componentDidMount() {
        const { textureResource, video, thumbnailUrl } = this.props
        const loader = new THREE.TextureLoader()
        loader.crossOrigin = 'anonymous'
        if (video) {
            this.loadVideoMaterial(textureResource)
            // Texture callback wasn't being properly triggered in safari when invoked via load function
            Promise.all([loader.load(thumbnailUrl)]).then(texture => {
                this.textureLoaded(texture)
                this.thumbnailTexture = texture
            })
        } else {
            Promise.all([loader.load(textureResource)]).then(this.textureLoaded)
        }
    }

    componentDidUpdate() {
        const { currentState } = this.props
        // Toggle between panning and default animation based on panning state
        // Use of currentlyPanning boolean prevents throttling of animation on update
        if (currentState === 'panning' && !this.currentlyPanning) {
            this.currentlyPanning = true
            this.setPanningAnimation(true)
        } else if (currentState !== 'panning' && this.currentlyPanning) {
            this.currentlyPanning = false
            this.setPanningAnimation(false)
        }
    }

    loadVideoMaterial = textureResource => {
        this.video = document.createElement('video')
        this.video.src = textureResource
        this.video.crossOrigin = 'anonymous'

        this.videoTexture = new THREEx.VideoTexture(this.video)
        this.videoTexture.minFilter = THREE.LinearFilter
        this.videoTexture.magFilter = THREE.LinearFilter
        this.videoTexture.format = THREE.RGBFormat
        this.videoTexture.needsUpdate = true

        this.videoMaterial = new THREE.MeshBasicMaterial({
            map: this.videoTexture,
        })
    }

    textureLoaded = texture => {
        const { video } = this.props

        const loadedMaterial = material(texture[0])
        if (video) {
            this.thumbnailMaterial = loadedMaterial
        }
        this.setState({
            texture: texture[0],
            material: loadedMaterial,
        })
    }

    onMeshMount = obj => {
        const { camera, renderer, scene, radius, reflection } = this.props // TODO: Ideally this should be passed down by context
        // TODO: DomEvent listeners should be bound inside Mesh and not here
        this.domEvents = new DomEvents(camera, renderer.domElement)
        if (!this.rootObj) {
            this.rootObj = obj
        }
        this.domEvents.addEventListener(obj, 'click', this.handleClick, false)
        this.domEvents.addEventListener(obj, 'touchstart', this.handleClick, false)
        this.domEvents.addEventListener(obj, 'touchend', this.handleClick, false)
        this.domEvents.addEventListener(obj, 'mouseover', this.handleMouseOver.bind(this), false)
        this.domEvents.addEventListener(obj, 'mouseout', this.handleMouseOut.bind(this), false)

        const geometry = new THREE.SphereGeometry(radius, 32, 16)

        // https://github.com/mrdoob/three.js/issues/1051
        // dynamic used for swapping materials
        geometry.dynamic = true
        obj.geometry = geometry

        if (reflection) {
            obj.rotation.y = 3.141593
            obj.material.opacity = 0.5
            obj.material.transparent = true
        }

        this.sceneObj = obj
        scene.add(obj)

        this.mixer.mesh = new THREE.AnimationMixer(obj)

        var expandScaleHoverKF = new THREE.VectorKeyframeTrack('.scale', [0, 0.25], [1, 1, 1, 2, 2, 2])
        var hoverClip = new THREE.AnimationClip('hovering', 0.25, [expandScaleHoverKF])
        this.actions.mesh.hoverClip = this.mixer.mesh.clipAction(hoverClip)
        this.actions.mesh.hoverClip.loop = THREE.LoopOnce
        this.actions.mesh.hoverClip.clampWhenFinished = true

        var decreaseScaleKF = new THREE.VectorKeyframeTrack('.scale', [0, 0.25], [2, 2, 2, 1, 1, 1])
        var hoverExitClip = new THREE.AnimationClip('default', 0.25, [decreaseScaleKF])
        this.actions.mesh.hoverExitClip = this.mixer.mesh.clipAction(hoverExitClip)
        this.actions.mesh.hoverExitClip.loop = THREE.LoopOnce
        this.actions.mesh.hoverExitClip.clampWhenFinished = true

        var expandScalePanningKF = new THREE.VectorKeyframeTrack('.scale', [0, 0.35], [1, 1, 1, 1.5, 1.5, 1.5])
        var panningClip = new THREE.AnimationClip('panning', 0.35, [expandScalePanningKF])
        this.actions.mesh.panningClip = this.mixer.mesh.clipAction(panningClip)
        this.actions.mesh.panningClip.loop = THREE.LoopOnce
        this.actions.mesh.panningClip.clampWhenFinished = true

        var decreaseScalePanningKF = new THREE.VectorKeyframeTrack('.scale', [0, 0.35], [1.5, 1.5, 1.5, 1, 1, 1])
        var reversePanningClip = new THREE.AnimationClip('panning', 0.35, [decreaseScalePanningKF])
        this.actions.mesh.reversePanningClip = this.mixer.mesh.clipAction(reversePanningClip)
        this.actions.mesh.reversePanningClip.loop = THREE.LoopOnce
        this.actions.mesh.reversePanningClip.clampWhenFinished = true

        this.animate() // TODO: this should handled in the renderer animate loop
    }

    playHoverAnimation = () => {
        this.actions.mesh.hoverClip.play()
        // Stop other animations after initiating anim, not before, to avoid flash back to default values before initiating delta
        this.actions.mesh.panningClip && this.actions.mesh.panningClip.stop()
        this.actions.mesh.hoverExitClip && this.actions.mesh.hoverExitClip.stop()
        this.actions.mesh.reversePanningClip && this.actions.mesh.reversePanningClip.stop()
        this.actions.mesh.panningClip && this.actions.mesh.panningClip.stop()
    }

    playHoverExitAnimation = () => {
        this.actions.mesh.hoverExitClip.play()
        // Stop other animations after initiating anim, not before, to avoid flash back to default values before initiating delta
        this.actions.mesh.panningClip && this.actions.mesh.panningClip.stop()
        this.actions.mesh.hoverClip && this.actions.mesh.hoverClip.stop()
        this.actions.mesh.reversePanningClip && this.actions.mesh.reversePanningClip.stop()
        this.actions.mesh.panningClip && this.actions.mesh.panningClip.stop()
    }

    setPanningAnimation = panningState => {
        if (panningState) {
            this.actions.mesh.panningClip.timeScale = 1
            this.actions.mesh.panningClip.play()
            // Stop other animations after initiating hover exit, not before, to avoid flash back to scale===1 before initiating scale decrease
            this.actions.mesh.hoverExitClip && this.actions.mesh.hoverExitClip.stop()
            this.actions.mesh.hoverClip && this.actions.mesh.hoverClip.stop()
            this.actions.mesh.reversePanningClip && this.actions.mesh.reversePanningClip.stop()
        } else {
            this.actions.mesh.reversePanningClip.play()
            // Stop other animations after initiating hover exit, not before, to avoid flash back to scale===1 before initiating scale decrease
            this.actions.mesh.hoverExitClip && this.actions.mesh.hoverExitClip.stop()
            this.actions.mesh.hoverClip && this.actions.mesh.hoverClip.stop()
            this.actions.mesh.panningClip && this.actions.mesh.panningClip.stop()
        }
    }

    onMeshUnmount = obj => {
        const { scene } = this.props

        this.domEvents && this.domEvents.destroy()
        this.raf && cancelAnimationFrame(this.raf)

        if (this.video) {
            this.video.pause()
            this.video.src = ''
            this.video.load()
        }

        dispose3DObject(obj)
        scene.remove(obj)
    }

    animate = _ => {
        const { video, reflection, camera } = this.props

        var delta = this.clock.getDelta()
        Object.keys(this.mixer).forEach(key => {
            this.mixer[key] && this.mixer[key].update(delta)
        })

        if (!reflection) {
            //     // Keeps video orb facing camera so proper video face is rendered
            //     // Need to figure out why I have to offset by 1.6 (approximately 90 degrees).  May have to do with startLon/startLat value
            this.sceneObj.rotation.y =
                Math.atan2(camera.position.x - this.sceneObj.position.x, camera.position.z - this.sceneObj.position.z) -
                1.6
        }
        if (video && this.videoTexture) {
            // Something is preventing video texture from setting its own needsUpdate in legacy panoplayer context
            // This is work-around
            this.videoTexture.needsUpdate = true
        }
        this.raf = requestAnimationFrame(this.animate)
    }

    handleClick = e => {
        const { onClick, mouseOutCallback, video, reflection } = this.props

        // Exits hover for both reflection and image/video sphere
        this.playHoverExitAnimation()

        // Stops video playback if video trigger
        if (video) {
            this.video.pause()
            this.video.currentTime = 0
            this.sceneObj.material = this.thumbnailMaterial
        }

        // Propagate click if not reflection orb
        if (!reflection) {
            mouseOutCallback && mouseOutCallback()
            onClick && onClick(e)
        }
    }

    handleMouseOver = e => {
        const { video, mouseOverCallback } = this.props
        this.setState({ isHovering: true })
        // Check if media is video and play if so
        this.playHoverAnimation()
        video && this.playTriggerVideo()
        mouseOverCallback && mouseOverCallback()
    }

    playTriggerVideo = () => {
        this.video.currentTime = 0
        this.sceneObj.material = this.videoMaterial
        this.video.play()
    }

    handleMouseOut = e => {
        const { video, mouseOutCallback } = this.props
        this.setState({ isHovering: false })
        // Check if media is video and pause if so
        this.playHoverExitAnimation()
        video && this.restartTriggerVideo()
        mouseOutCallback && mouseOutCallback()
    }

    restartTriggerVideo = () => {
        this.video.pause()
        this.video.currentTime = 0
        this.sceneObj.material = this.thumbnailMaterial
    }

    render() {
        const { texture, material, geometry, scale, isHovering } = this.state
        const { position, currentState, scene, camera, renderer, reflection, radius, isMobile, ...props } = this.props
        return texture && material && geometry ? (
            <Fragment>
                <Mesh
                    geometry={geometry}
                    material={material}
                    position={position}
                    scale={scale}
                    onMount={this.onMeshMount}
                    onWillUnmount={this.onMeshUnmount}
                />
                {!reflection && currentState === 'visited' && (
                    <Sprite3D
                        rootObj={this.rootObj}
                        scene={scene}
                        camera={camera}
                        renderer={renderer}
                        spriteResource={CHECK_CIRCLE}
                        offsetCofactor={{
                            x: 0.1,
                            y: 0.1,
                            z: -0.1,
                        }}
                    />
                )}
                {!reflection && this.rootObj && (
                    <TriggerText3D
                        {...props}
                        rootObj={this.rootObj}
                        isVisible={isHovering}
                        camera={camera}
                        key={'descriptionText'}
                        triggerType={'sphere'}
                    />
                )}
                {!reflection && this.rootObj && (
                    <HitSphere3D
                        rootObj={this.rootObj}
                        radius={radius * 1.1}
                        opacity={0}
                        color={'white'}
                        handleClick={this.handleClick}
                        handleTouchStart={this.handleClick}
                        // handleTouchEnd={this.handleClick}
                        handleMouseOut={this.handleMouseOut}
                        handleMouseOver={this.handleMouseOver}
                        camera={camera}
                        renderer={renderer}
                        isMobile={isMobile}
                    />
                )}
                {this.props.children}
            </Fragment>
        ) : null
    }
}
Trigger3DBaseSphere.defaultProps = {
    overAnimationTime: 150,
    outAnimationTime: 250,
    handleClick: () => {
        console.log('clicked 3DTriggerBase')
    },
}

export default Trigger3DBaseSphere
